import { useState, useEffect } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/dashboard/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, Pencil, Trash2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { canManageModule } from "@/lib/permissions";
import { ServerPagination } from "@/components/ui/server-pagination";

const Discipline = () => {
  const { roles } = useAuth();
  const canManage = canManageModule(roles, "discipline");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState({ student_id: "", incident_date: "", category: "", severity: "minor", description: "", status: "open", sanction: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setCurrentPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ["discipline-cases", debouncedSearch, currentPage, pageSize],
    queryFn: async () => {
      let q = supabase.from("discipline_cases").select("*, students(first_name, last_name, student_number)", { count: "exact" }).order("created_at", { ascending: false });
      const from = (currentPage - 1) * pageSize;
      q = q.range(from, from + pageSize - 1);
      const { data, error, count } = await q;
      if (error) throw error;
      let filtered = data ?? [];
      if (debouncedSearch) {
        const s = debouncedSearch.toLowerCase();
        filtered = filtered.filter((c: any) =>
          `${c.students?.first_name} ${c.students?.last_name} ${c.category}`.toLowerCase().includes(s)
        );
      }
      return { cases: filtered, totalCount: count ?? 0 };
    },
  });

  const { data: students = [] } = useQuery({
    queryKey: ["students-select"],
    queryFn: async () => {
      const { data } = await supabase.from("students").select("id, first_name, last_name, student_number").order("first_name");
      return data ?? [];
    },
  });

  const cases = data?.cases ?? [];
  const totalCount = data?.totalCount ?? 0;

  const saveMutation = useMutation({
    mutationFn: async (d: any) => {
      const payload = { student_id: d.student_id, incident_date: d.incident_date, category: d.category || null, severity: d.severity, description: d.description, status: d.status, sanction: d.sanction || null };
      if (d.id) {
        const { error } = await supabase.from("discipline_cases").update(payload).eq("id", d.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("discipline_cases").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["discipline-cases"] });
      toast({ title: editing ? "Case updated" : "Case reported" });
      setFormOpen(false);
      setEditing(null);
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("discipline_cases").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["discipline-cases"] });
      toast({ title: "Case deleted" });
    },
  });

  const openNew = () => { setEditing(null); setForm({ student_id: "", incident_date: new Date().toISOString().split("T")[0], category: "", severity: "minor", description: "", status: "open", sanction: "" }); setFormOpen(true); };
  const openEdit = (c: any) => { setEditing(c); setForm({ student_id: c.student_id, incident_date: c.incident_date, category: c.category || "", severity: c.severity || "minor", description: c.description, status: c.status, sanction: c.sanction || "" }); setFormOpen(true); };

  return (
    <AppLayout>
      <div className="space-y-3 sm:space-y-4">
        <div className="page-header">
          <div>
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold">Discipline</h1>
            <p className="text-muted-foreground text-sm">Manage student conduct and disciplinary cases</p>
          </div>
          {canManage && <Button onClick={openNew}><Plus className="mr-2 h-4 w-4" />Report Incident</Button>}
        </div>

        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Edit Case" : "Report Incident"}</DialogTitle></DialogHeader>
            <form onSubmit={e => { e.preventDefault(); saveMutation.mutate({ ...form, id: editing?.id }); }} className="space-y-4">
              <div><Label>Student *</Label>
                <Select value={form.student_id} onValueChange={v => setForm(f => ({ ...f, student_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select student" /></SelectTrigger>
                  <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.student_number})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Incident Date *</Label><Input type="date" value={form.incident_date} onChange={e => setForm(f => ({ ...f, incident_date: e.target.value }))} required /></div>
                <div><Label>Category</Label><Input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} placeholder="e.g. Misconduct" /></div>
                <div><Label>Severity</Label>
                  <Select value={form.severity} onValueChange={v => setForm(f => ({ ...f, severity: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minor">Minor</SelectItem>
                      <SelectItem value="moderate">Moderate</SelectItem>
                      <SelectItem value="major">Major</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Status</Label>
                  <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="investigation">Investigation</SelectItem>
                      <SelectItem value="hearing">Hearing</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Description *</Label><Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} required /></div>
              <div><Label>Sanction</Label><Input value={form.sanction} onChange={e => setForm(f => ({ ...f, sanction: e.target.value }))} placeholder="e.g. Written Warning" /></div>
              <Button type="submit" disabled={saveMutation.isPending} className="w-full">{saveMutation.isPending ? "Saving..." : "Save"}</Button>
            </form>
          </DialogContent>
        </Dialog>

        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search cases..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Discipline Cases</CardTitle>
            <CardDescription>{totalCount} cases</CardDescription>
          </CardHeader>
          <CardContent className="table-responsive">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Student</TableHead>
                  <TableHead>Incident Date</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sanction</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cases.map((c: any) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.students?.first_name} {c.students?.last_name}</TableCell>
                    <TableCell>{c.incident_date}</TableCell>
                    <TableCell>{c.category || "—"}</TableCell>
                    <TableCell><StatusBadge status={c.severity || "minor"} /></TableCell>
                    <TableCell><StatusBadge status={c.status} /></TableCell>
                    <TableCell>{c.sanction || "—"}</TableCell>
                    <TableCell className="flex gap-1">
                      {canManage && <Button variant="ghost" size="sm" onClick={() => openEdit(c)}><Pencil className="h-3 w-3" /></Button>}
                      {canManage && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button variant="ghost" size="sm"><Trash2 className="h-3 w-3 text-destructive" /></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Delete case?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => deleteMutation.mutate(c.id)}>Delete</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {cases.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">{isLoading ? "Loading..." : "No cases found"}</TableCell></TableRow>}
              </TableBody>
            </Table>
            <ServerPagination currentPage={currentPage} pageSize={pageSize} totalCount={totalCount} onPageChange={setCurrentPage} onPageSizeChange={setPageSize} />
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default Discipline;
